import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Input, Button, Row, Col, Table, Form, Label } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import Pagination from '../../../../components/Pagination';
import { TableContainer } from '../FilterClientScreen/styled';
import ReactLoader from 'components/Loader';
import { toast } from 'react-toastify';
import { useClienteSearch } from '../FilterClientScreen/useClientSearch';
import { useAccounts } from 'hooks/useAccounts';
import { FlexController } from 'components/FlexControllerInput';
import { useRecordTypesByEntity } from 'hooks/useRecordTypesByEntity';
import { useSelector } from 'react-redux';
import contactService from 'views/CRM/Contact/services/contactService';
import ModalConfirmation from 'views/ServicePanel/components/QueueContent/components/Contact/components/ModalConfirmation/index.js';

const availableSearchTypes = {
  INPUT_TEXT: 'INPUT_TEXT',
  SELECT_BOX: 'SELECT_BOX',
  INPUT_NUMBER: 'INPUT_NUMBER'
};

const FilterClientBroadcastList = ({
  setSelectedClient,
  account = false,
  selectCurrent = []
}) => {
  const { control, getValues, setValue, watch } = useForm();
  const { fieldsConfig, searchClient, loading } = useClienteSearch({ account });
  const [clients, setClients] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchType, setSearchType] = useState(availableSearchTypes.INPUT_TEXT);
  const [currentFieldsToSelectBox, setCurrentFieldsToSelectBox] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const { recordTypesOptions } = useRecordTypesByEntity('ACCOUNT');
  const selectedRecordTypeId = watch('recordTypeId')?.value ?? 0;
  const selectedAccountsArray = watch('searchValue');
  const [clientsSelectedBySelectAll, setClientsSelectedBySelectAll] = useState(
    []
  );
  const selectedAccountsIds = useMemo(() => {
    if (!Array.isArray(selectedAccountsArray)) return [];
    return selectedAccountsArray.map((acc) => acc.value);
  }, [JSON.stringify(selectedAccountsArray)]);

  const { accountsOptions } = useAccounts({
    recordTypeId: selectedRecordTypeId,
    sortConfig: { key: 'name', direction: 'ascending' }
  });

  const lastAttributeAtClientSearch = localStorage.getItem(
    'flexuc:lastAttributeAtClientSearch'
  );

  const fieldNameIsNumericType = useCallback(
    (fieldName) => {
      return (
        fieldsConfig?.filter((field) => field.name === fieldName)?.type ===
        'NUMERIC'
      );
    },
    [fieldsConfig]
  );

  const handleInputKeyChange = useCallback(
    (fieldName) => {
      localStorage.setItem('flexuc:lastAttributeAtClientSearch', fieldName);

      const isNumberField = fieldNameIsNumericType(fieldName);
      const isSelectBoxField =
        fieldsConfig.find((field) => field.name === fieldName)?.type ===
          'LIST' || fieldName === 'accountId';

      if (isSelectBoxField && !isNumberField) {
        setSearchType(availableSearchTypes.SELECT_BOX);
        const options = fieldsConfig.filter(
          (field) => field.name === fieldName
        );

        const newOptions =
          (options &&
            !!options.length &&
            options[0].options.map((op) => ({
              label: op.label,
              value: op.value
            }))) ||
          [];

        if (fieldName === 'accountId')
          return setCurrentFieldsToSelectBox(accountsOptions);
        return setCurrentFieldsToSelectBox([...newOptions]);
      } else if (!isSelectBoxField && !isNumberField) {
        setSearchType(availableSearchTypes.INPUT_TEXT);
        setValue('searchValue', '');
        return setCurrentFieldsToSelectBox([]);
      } else {
        setSearchType(availableSearchTypes.INPUT_NUMBER);
        setValue('searchValue', 0);
        return setCurrentFieldsToSelectBox([]);
      }
    },
    [fieldsConfig, setValue, fieldNameIsNumericType, accountsOptions]
  );

  useEffect(() => {
    if (!fieldsConfig?.length || !lastAttributeAtClientSearch) return;

    setValue('searchKey', lastAttributeAtClientSearch);
    handleInputKeyChange(lastAttributeAtClientSearch);
  }, [
    fieldsConfig,
    handleInputKeyChange,
    lastAttributeAtClientSearch,
    setValue
  ]);

  const getCustomFieldsNameAndLabel = () => {
    if (!fieldsConfig?.length) return [];

    return fieldsConfig
      .filter((field) => ['TEXT', 'NUMERIC', 'LIST'].includes(field.type))
      .map((field) => ({ name: field.name, label: field.label }));
  };

  const onSearch = useCallback(
    async (page = 1) => {
      const { searchKey, searchValue } = getValues();

      if (!searchKey || !searchValue) {
        return setClients([]);
      }

      const searchValueToSend = Array.isArray(searchValue)
        ? searchValue.map((item) => item.value)
        : searchValue;

      const resultSet = await searchClient({
        attribute: searchKey,
        value: searchValueToSend,
        page,
        disableOrderBy: true
      });

      const total =
        (resultSet && resultSet.quantity && Number(resultSet.quantity)) || 0;
      setTotalItems(total);

      setClients(resultSet?.content || []);
    },
    [getValues, searchClient]
  );

  useEffect(() => {
    onSearch(activePage);
  }, [activePage, onSearch]);

  useEffect(() => {
    if (selectCurrent) {
      setSelectedClients(selectCurrent);
    }
  }, [selectCurrent]);

  useEffect(() => {
    if (isSelectAll) return setSelectedClients([]);

    setSelectedClients(
      // eslint-disable-next-line no-unused-vars
      clientsSelectedBySelectAll.map(({ recordTypeId, ...rest }) => rest)
    );
  }, [clientsSelectedBySelectAll]);

  useEffect(() => {
    const areTheyEqual =
      JSON.stringify(selectedClients.map((acc) => acc.id).sort()) ===
      JSON.stringify(clientsSelectedBySelectAll.map((acc) => acc.id).sort());
    setIsSelectAll(areTheyEqual && clientsSelectedBySelectAll.length);
  }, [selectedClients]);

  const handlePageChange = (newPage) => {
    setActivePage(newPage);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    onSearch();
  };

  const handleSelectAllOffPage = (isChecked) => {
    if (isChecked) {
      const visibleClients = clients.map((client) => ({
        id: client.id,
        name: client.nome || client.name
      }));
      setSelectedClients((prevSelected) => {
        const newSelection = [...prevSelected, ...visibleClients];
        return newSelection.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );
      });
    } else {
      const visibleClientIds = clients.map((client) => client.id);
      setSelectedClients((prevSelected) =>
        prevSelected.filter((item) => !visibleClientIds.includes(item.id))
      );
    }
  };

  const handleSelectAll = async () => {
    const allClients = await contactService.getContactsByAccountsId(
      selectedAccountsIds
    );

    setClientsSelectedBySelectAll(allClients?.data?.data);

    setIsSelectAll(!isSelectAll);
  };

  const handleSelectIndividual = (clientId, clientName, phone, checked) => {
    setSelectedClients((prevSelected) => {
      if (checked) {
        const newClient = {
          id: clientId,
          name: clientName,
          phoneNumber: phone
        };
        return [...prevSelected, newClient];
      } else {
        setIsSelectAll(false);
        return prevSelected.filter((item) => item.id !== clientId);
      }
    });
  };

  const handleSaveAndClose = () => {
    if (selectedClients.length > 0) {
      setShowModalConfirmation(true);
    } else {
      toast.error('Nenhum cliente selecionado.');
    }
  };

  const confirmSelection = () => {
    if (selectedClients.length === 0) {
      toast.error('Você precisa selecionar pelo menos um cliente!');
      return;
    }
    setShowModalConfirmation(false);

    selectedClients.forEach((client) => {
      setSelectedClient({
        clientId: client.id,
        clientName: client.name,
        clientPhone: client.phoneNumber ?? client.phone ?? client.telefone
      });
    });
  };

  const cancelSelection = () => {
    setShowModalConfirmation(false);
  };

  const handleDelete = (clientId) => {
    const updatedClients = selectedClients.filter(
      (client) => client.id !== clientId
    );
    setSelectedClients(updatedClients);
  };

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const [isSelectAll, setIsSelectAll] = useState(false);

  return (
    <>
      <Form
        name="formSearchClient"
        onSubmit={onSubmit}
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 8fr max-content',
          columnGap: '1rem',
          marginBottom: '1.5rem'
        }}
      >
        <div>
          <Controller
            as={
              <Input
                type="select"
                name="searchKey"
                onChangeCapture={(event) =>
                  handleInputKeyChange(event.target.value)
                }
              >
                <option value="">Buscar por ...</option>
                <option value={account ? 'name' : 'nome'}>Nome</option>
                {!account && <option value="email">E-mail</option>}
                <option value="accountId">Conta</option>
                <option value={account ? 'telephone' : 'telefone'}>
                  Telefone
                </option>
                {Object.keys(fieldsConfig).length &&
                  fieldsConfig
                    .filter((field) =>
                      ['TEXT', 'NUMERIC', 'LIST', 'CPF', 'CNPJ'].includes(
                        field.type
                      )
                    )
                    .map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.label}
                      </option>
                    ))}
              </Input>
            }
            control={control}
            name="searchKey"
            defaultValue=""
          />
        </div>
        <div>
          {searchType === availableSearchTypes.INPUT_TEXT && (
            <Controller
              as={Input}
              control={control}
              defaultValue={''}
              name="searchValue"
            />
          )}
          <Row>
            <Col md="5">
              {getValues('searchKey') === 'accountId' ? (
                <FlexController
                  textoDoLabel="Escolha uma opção"
                  type="selectable"
                  name="recordTypeId"
                  options={recordTypesOptions}
                  placeholder="Tipo de registro - Contas"
                  valueController={setValue}
                  isMulti={false}
                  closeMenuOnSelect
                  isClearable={false}
                  isSearchable
                  portalTarget={false}
                  hideLabel
                  errors={{}}
                  control={control}
                />
              ) : null}
            </Col>

            {searchType === availableSearchTypes.SELECT_BOX && (
              <Col md="7">
                <FlexController
                  textoDoLabel="Escolha uma conta"
                  type="selectable"
                  name="searchValue"
                  options={currentFieldsToSelectBox}
                  placeholder="Selecione uma conta"
                  valueController={setValue}
                  noOptionsMessage="Selecione um tipo de registro para as contas. Após selecionado, as opções de conta aparecerão aqui."
                  isMulti={true}
                  closeMenuOnSelect
                  isClearable={false}
                  isSearchable
                  portalTarget={false}
                  hideLabel
                  errors={{}}
                  control={control}
                />
              </Col>
            )}
          </Row>

          {searchType === availableSearchTypes.INPUT_NUMBER && (
            <Controller
              as={Input}
              type="number"
              control={control}
              defaultValue={0}
              name="searchValue"
            />
          )}
        </div>

        <div>
          <Button
            type="submit"
            style={{
              width: '100%',
              color: '#fff',
              border: 'none',
              background: systemConfiguration.primary_color
            }}
          >
            Buscar
          </Button>
        </div>
      </Form>
      <Label
        style={{
          cursor: isSelectAll || totalItems < 1 ? 'auto' : 'pointer',
          marginLeft: '1.25rem'
        }}
        check
      >
        <Input
          type="checkbox"
          checked={isSelectAll}
          onClick={handleSelectAll}
          disabled={totalItems < 1}
        />
        <span
          style={{ userSelect: 'none' }}
        >{`Selecionar todos (${totalItems})`}</span>
      </Label>
      <div id="listClientsTable">
        <TableContainer
          style={{
            overflowX: 'auto'
          }}
        >
          {loading ? (
            <Row className="justify-content-md-center py-3">
              <ReactLoader />
            </Row>
          ) : (
            <Table hover={true}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      style={{ cursor: 'pointer' }}
                      onChange={(e) => handleSelectAllOffPage(e.target.checked)}
                    />
                  </th>
                  <th>Nome</th>
                  {!account && <th>Email</th>}
                  <th>Telefone</th>
                  <th>Conta</th>
                  {getCustomFieldsNameAndLabel().map((field) => (
                    <th key={field.name}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.length
                  ? clients.map((client) => (
                      <tr
                        key={client.id}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          if (e.target.tagName !== 'INPUT') {
                            handleSelectIndividual(
                              client.id,
                              client.nome || client.name,
                              client.telefone,
                              !selectedClients.some(
                                (selected) => selected.id === client.id
                              )
                            );
                          }
                        }}
                      >
                        <td>
                          <input
                            type="checkbox"
                            style={{ cursor: 'pointer' }}
                            checked={selectedClients.some(
                              (selected) => selected.id === client.id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectIndividual(
                                client.id,
                                client.nome || client.name,
                                client.telefone,
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td>{client.nome || client.name}</td>
                        {!account && <td>{client.email}</td>}
                        <td>{client.telefone || client.telephone}</td>
                        {getCustomFieldsNameAndLabel().map((field) => (
                          <td key={field.name}>{client[field.name]}</td>
                        ))}
                      </tr>
                    ))
                  : null}
              </tbody>
            </Table>
          )}
        </TableContainer>
        {clients.length > 0 && totalItems > 10 && (
          <Pagination
            activePage={activePage}
            totalItemsCount={totalItems}
            onChange={handlePageChange.bind(this)}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {selectedClients.length > 0 && (
          <Button
            color="success"
            onClick={handleSaveAndClose}
            className="btn-block"
            style={{ marginTop: '1rem', width: '20%' }}
          >
            Salvar
          </Button>
        )}
      </div>
      <ModalConfirmation
        isOpen={showModalConfirmation}
        clients={selectedClients}
        onConfirm={confirmSelection}
        onCancel={cancelSelection}
        onDelete={handleDelete}
      />
    </>
  );
};

export default FilterClientBroadcastList;
